import React, { useEffect, useState } from 'react';
import { Loader2, Trash2, CheckCircle2, Send, AlertCircle, Mail, Server, Check, ExternalLink, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import EmailMailboxStatus from './email/EmailMailboxStatus';
import EmailSmtpForm from './email/EmailSmtpForm';
import EmailSignatureEditor from './email/EmailSignatureEditor';
import SharedMailboxesPanel from './email/SharedMailboxesPanel';
import UniversalSignatureEditor from './email/UniversalSignatureEditor';
import MicrosoftConnectCard from './MicrosoftConnectCard';
import { oauthRedirect } from '../utils/oauthRedirect';

/**
 * SMTP mailbox configuration section.
 * Two scopes are supported: org-wide (admin-only) + per-user override.
 * Sends live test emails before save via POST /api/email-configs/test.
 *
 * Vague 97.3 — orchestrator only. Form rendering split into 3 sub-components
 * (status banner, SMTP form, signature editor) under /components/email/.
 */
export default function EmailMailboxSection({ currentUser, restrictTo = null }) {
  // `restrictTo`: null (default = both scope tabs) | 'user' | 'org'
  // Used by Profil.jsx (restrictTo='user') and Paramètres (restrictTo='org')
  // to avoid duplicate UX since Option A separates personal vs org settings.
  const [providers, setProviders] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState(restrictTo || 'user'); // 'user' | 'org'
  const [form, setForm] = useState({
    provider: 'custom',
    host: '', port: 587, security: 'starttls',
    imap_host: '', imap_port: 993, imap_security: 'ssl',
    username: '', password: '',
    from_email: '', from_name: currentUser?.name || '',
    active: true,
    signature_text: '',
    signature_html: '',
    signature_image_b64: null, // null = keep existing on save
    signature_image_mime: '',
    signature_image_set: false, // read-only flag (populated after first save)
    signature_image_width: 320,
  });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState(currentUser?.email || '');
  const [testResult, setTestResult] = useState(null); // { ok, message }
  const [signaturePreview, setSignaturePreview] = useState(null); // base64 preview URL
  const [googlePrefs, setGooglePrefs] = useState(null);
  const [googleCalendars, setGoogleCalendars] = useState([]);
  const [sendMode, setSendMode] = useState(null); // 'gmail' | 'smtp' — derived from prefs on load
  const [savingPref, setSavingPref] = useState(null);
  const isAdmin = (currentUser?.role || '').toLowerCase().startsWith('admin');

  // Vague 96.13 — strict per-user filter. Admins receive all org configs from
  // GET /api/email-configs but must only edit their own user-scoped one.
  const myUid = currentUser?.user_id;
  const findExisting = (list) => (list || []).find((x) => (
    scope === 'user' ? (x.user_id && (!myUid || x.user_id === myUid)) : !x.user_id
  ));

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, c, a] = await Promise.all([
        api.get('/email-configs/providers'),
        api.get('/email-configs'),
        api.get('/email-configs/active'),
      ]);
      setProviders(p.data);
      setConfigs(c.data);
      setActive(a.data);
      const existing = findExisting(c.data);
      if (existing) {
        // Fetch full signature text (not returned by the list endpoint to keep it light)
        let sigDetails = { signature_text: '', signature_image_set: false };
        try {
          const r = await api.get(`/email-configs/${existing.id}/details`);
          sigDetails = r.data;
        } catch (e) { console.warn("[silent]", e); }
        setForm({
          provider: existing.provider || 'custom',
          host: existing.host || '', port: existing.port || 587,
          security: existing.security || 'starttls',
          imap_host: existing.imap_host || '',
          imap_port: existing.imap_port || 993,
          imap_security: existing.imap_security || 'ssl',
          username: existing.username || '',
          password: '', // never returned — user keeps existing unless they retype
          from_email: existing.from_email || '',
          from_name: existing.from_name || currentUser?.name || '',
          active: existing.active !== false,
          signature_text: sigDetails.signature_text || '',
          signature_html: sigDetails.signature_html || '',
          signature_image_b64: null, // keep existing on server unless user re-uploads
          signature_image_mime: sigDetails.signature_image_mime || '',
          signature_image_set: !!sigDetails.signature_image_set,
          signature_image_width: sigDetails.signature_image_width || 320,
        });
        if (sigDetails.signature_image_set) {
          try {
            const img = await api.get(`/email-configs/${existing.id}/signature-image`, { responseType: 'blob' });
            setSignaturePreview(URL.createObjectURL(img.data));
          } catch (e) { console.warn("[silent]", e); }
        } else {
          setSignaturePreview(null);
        }
      } else {
        setSignaturePreview(null);
      }
    } catch (e) { console.warn("[silent]", e); }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, [scope]);

  // Google prefs + agenda list (user scope only).
  const loadGoogle = async () => {
    try {
      const { data } = await api.get('/integrations/google/prefs');
      setGooglePrefs(data);
      // Derive the default send mode from persisted prefs (first render only).
      setSendMode((cur) => cur || (data.connected && data.prefer_gmail_send && data.has_gmail_scope ? 'gmail' : 'smtp'));
      if (data.connected && data.has_calendar_scope) {
        try {
          const cals = await api.get('/integrations/google/calendars');
          setGoogleCalendars(cals.data?.calendars || []);
        } catch { setGoogleCalendars([]); }
      } else {
        setGoogleCalendars([]);
      }
    } catch { setGooglePrefs(null); }
  };
  useEffect(() => {
    if (scope === 'user') loadGoogle();
    else { setSendMode('smtp'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const updateGooglePref = async (patch, key) => {
    setSavingPref(key);
    try {
      const { data } = await api.put('/integrations/google/prefs', patch);
      setGooglePrefs(data);
      if (patch.prefer_gmail_send !== undefined) {
        setSendMode(patch.prefer_gmail_send ? 'gmail' : 'smtp');
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSavingPref(null); }
  };

  const applyPreset = (id) => {
    const p = providers.find((x) => x.id === id);
    setForm((s) => ({
      ...s, provider: id,
      host: p?.host || s.host, port: p?.port || s.port, security: p?.security || s.security,
      imap_host: p?.imap_host || s.imap_host,
      imap_port: p?.imap_port || s.imap_port,
      imap_security: p?.imap_security || s.imap_security,
    }));
  };

  // ----- Test IMAP connection (Vague 113 L10 polish) -----
  const [testingImap, setTestingImap] = useState(false);
  const [testImapResult, setTestImapResult] = useState(null);
  const handleTestImap = async () => {
    setTestingImap(true);
    setTestImapResult(null);
    try {
      const payload = {
        host: form.imap_host || form.host?.replace('smtp.', 'imap.') || '',
        port: Number(form.imap_port || 993),
        security: form.imap_security || 'ssl',
        username: form.username,
        password: form.password || null,
        cfg_id: existing?.id || null,
      };
      const { data } = await api.post('/email-configs/test-imap', payload);
      setTestImapResult(data);
    } catch (err) {
      setTestImapResult({ ok: false, step: 'request', detail: err?.response?.data?.detail || String(err) });
    } finally {
      setTestingImap(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) { toast({ title: 'Adresse de test requise' }); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const existing = findExisting(configs);
      if (form.password) {
        await api.post('/email-configs/test', {
          to: testEmail,
          host: form.host, port: Number(form.port), security: form.security,
          username: form.username, password: form.password,
          from_email: form.from_email, from_name: form.from_name,
        });
      } else if (existing) {
        await api.post('/email-configs/test', { to: testEmail, config_id: existing.id });
      } else {
        setTestResult({ ok: false, message: 'Saisissez votre mot de passe SMTP avant de lancer le test.' });
        setTesting(false);
        return;
      }
      setTestResult({ ok: true, message: `Email de test envoyé à ${testEmail}. Vérifiez votre boîte de réception (et les spams).` });
      toast({ title: 'Email envoyé ✅' });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur inconnue — vérifiez vos identifiants et le port.';
      setTestResult({ ok: false, message: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!form.host || !form.username || !form.from_email) {
      toast({ title: 'Champs obligatoires manquants', description: 'Host, Username et Email d\'expédition sont requis' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form, scope,
        port: Number(form.port),
        imap_port: form.imap_port ? Number(form.imap_port) : 993,
      };
      delete payload.signature_image_set; // read-only flag
      await api.post('/email-configs', payload);
      toast({ title: 'Configuration enregistrée' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible' });
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    const existing = findExisting(configs);
    if (!existing) return;
    if (!window.confirm('Supprimer cette configuration ? Les emails sortiront via Resend (fallback).')) return;
    try {
      await api.delete(`/email-configs/${existing.id}`);
      toast({ title: 'Configuration supprimée' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail });
    }
  };

  const existing = findExisting(configs);
  const gmailAvailable = googlePrefs?.connected && googlePrefs?.has_gmail_scope;
  const effectiveMode = scope === 'org' ? 'smtp' : (sendMode || 'smtp');

  // Wave 1u — lift Microsoft status up so the header banner + switches share it
  const [msStatus, setMsStatus] = useState(null);
  useEffect(() => {
    api.get('/integrations/microsoft/status').then((r) => setMsStatus(r.data)).catch(() => {});
  }, [scope]);
  // Aggregate connections object for EmailMailboxStatus
  const connections = {
    gmail: gmailAvailable ? { email: googlePrefs?.email } : null,
    microsoft: msStatus?.connected ? { email: msStatus?.email } : null,
    smtp: existing ? { email: existing.from_email } : null,
  };

  // Wave 1u.1 — when the user clicks "Connecter" on SMTP/IMAP, expand the
  // provider preset cards + form just below the picker. Hidden by default.
  const [smtpFormOpen, setSmtpFormOpen] = useState(false);

  const handleConnectClick = async (id) => {
    if (id === 'google') {
      try {
        const { data } = await api.get('/integrations/google/authorize', {
          params: { return_origin: window.location.origin },
        });
        const target = data?.authorize_url || data?.auth_url;
        if (!target) throw new Error('URL OAuth manquante');
        oauthRedirect(target);
      } catch (err) {
        toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de démarrer la connexion Google.' });
      }
    } else if (id === 'microsoft') {
      try {
        const { data } = await api.get('/integrations/microsoft/start', {
          params: { return_origin: window.location.origin },
        });
        if (data?.auth_url) oauthRedirect(data.auth_url);
        else throw new Error('URL OAuth manquante');
      } catch (err) {
        toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de démarrer la connexion Microsoft.' });
      }
    } else if (id === 'smtp') {
      // Wave 1u.1 — open the SMTP form inline + scroll
      setSmtpFormOpen(true);
      setTimeout(() => {
        document.querySelector('[data-testid="email-smtp-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <div className="space-y-4" data-testid="email-mailbox-section">
      <EmailMailboxStatus active={active} connections={scope === 'user' ? connections : undefined} />

      {/* Wave 1u.1 — show MS card ONLY when MS is connected (management panel:
          sync calendar, push, disconnect). When NOT connected, the "Connecter"
          button lives in MailboxSwitches below to avoid duplicate UI. */}
      {restrictTo !== 'org' && msStatus?.connected && <MicrosoftConnectCard />}

      <div className="soft-card p-5">
        {/* Scope tabs (hidden when restrictTo is set — single-view mode) */}
        {!restrictTo && (
        <div className="flex bg-[#F6F7FB] rounded-xl p-1 mb-4 w-fit">
          <button
            type="button"
            data-testid="email-scope-user"
            onClick={() => setScope('user')}
            className={`h-9 px-4 rounded-lg text-[12.5px] font-semibold ${scope === 'user' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85]'}`}
          >
            Ma boîte personnelle
          </button>
          <button
            type="button"
            data-testid="email-scope-org"
            onClick={() => setScope('org')}
            disabled={!isAdmin}
            title={!isAdmin ? 'Réservé aux administrateurs' : ''}
            className={`h-9 px-4 rounded-lg text-[12.5px] font-semibold ${scope === 'org' ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-[#6B6C85]'} ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Boîte organisation (partagée)
          </button>
        </div>
        )}

        {loading ? (
          <div className="text-[13px] text-[#6B6C85] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : (
          <>
            {/* -------- Phase 4 — Shared org mailboxes (admin only) ----- */}
            {scope === 'org' && isAdmin && (
              <div className="mb-4">
                <SharedMailboxesPanel />
              </div>
            )}

            {/* -------- Wave 1u — Mailbox picker : 3 choices, one-active rule -------- */}
            {scope === 'user' && (
              <MailboxSwitches
                gmailAvailable={gmailAvailable}
                googleEmail={googlePrefs?.email}
                hasSmtp={!!findExisting(configs)}
                smtpEmail={findExisting(configs)?.from_email}
                isAdmin={isAdmin}
                msStatus={msStatus}
                onConnectClick={handleConnectClick}
              />
            )}

            {/* -------- Wave 1u.1 — Universal signature (works for ALL providers) -------- */}
            {scope === 'user' && (
              <div className="mb-5">
                <UniversalSignatureEditor />
              </div>
            )}

            {/* -------- Gmail mode panel (agenda selector) — Wave 1n: show whenever Gmail is connected & not disabled */}
            {scope === 'user' && gmailAvailable && (
              <div className="mb-5 rounded-xl border border-[#D1FAE5] bg-gradient-to-r from-[#ECFDF5] to-[#F0FDF4] p-4" data-testid="email-gmail-panel">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-[#065F46]">
                      Les emails partent via Gmail
                    </div>
                    <div className="text-[11.5px] text-[#047857] truncate">
                      Expéditeur&nbsp;: <span className="font-semibold">{googlePrefs?.email}</span>
                    </div>
                  </div>
                </div>

                {/* Calendar sync toggle + selector */}
                <div className="mt-3 pt-3 border-t border-[#D1FAE5] space-y-2.5">
                  <label className="flex items-center gap-2 text-[12.5px] text-[#064E3B] cursor-pointer" data-testid="email-gmail-calsync-toggle">
                    <input
                      type="checkbox"
                      checked={!!googlePrefs?.calendar_auto_sync && !!googlePrefs?.has_calendar_scope}
                      disabled={!googlePrefs?.has_calendar_scope || savingPref === 'calendar_auto_sync'}
                      onChange={(e) => updateGooglePref({ calendar_auto_sync: e.target.checked }, 'calendar_auto_sync')}
                    />
                    <Calendar className="w-3.5 h-3.5" />
                    Synchroniser automatiquement mes sessions vers Google Agenda
                  </label>

                  {googlePrefs?.calendar_auto_sync && googlePrefs?.has_calendar_scope && (
                    <div className="pl-5 space-y-3">
                      <div>
                        <label className="text-[11px] font-semibold text-[#064E3B] block mb-1">Agenda cible</label>
                        <select
                          value={googlePrefs?.calendar_id || 'primary'}
                          onChange={(e) => updateGooglePref({ calendar_id: e.target.value }, 'calendar_id')}
                          disabled={savingPref === 'calendar_id'}
                          data-testid="email-gmail-calendar-select"
                          className="w-full max-w-md h-9 px-3 rounded-lg border border-[#A7F3D0] bg-white text-[12.5px]"
                        >
                          {googleCalendars.length === 0 && (
                            <option value={googlePrefs?.calendar_id || 'primary'}>
                              {googlePrefs?.calendar_id || 'Agenda principal'}
                            </option>
                          )}
                          {googleCalendars.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.summary}{c.primary ? ' (principal)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Vague 113 L7 — Fine-grained sync mode toggles */}
                      <div className="space-y-1.5">
                        <label className="flex items-center gap-2 text-[12px] text-[#064E3B] cursor-pointer" data-testid="email-toggle-push-crm">
                          <input
                            type="checkbox"
                            checked={googlePrefs?.calendar_sync_crm_events !== false}
                            onChange={(e) => updateGooglePref({ calendar_sync_crm_events: e.target.checked }, 'crm_events')}
                            disabled={savingPref === 'crm_events'}
                          />
                          Inclure aussi mes <strong>événements CRM</strong> (RDV, relances, tâches)
                        </label>
                        <label className="flex items-center gap-2 text-[12px] text-[#064E3B] cursor-pointer" data-testid="email-toggle-pull">
                          <input
                            type="checkbox"
                            checked={!!googlePrefs?.pull_google_events}
                            onChange={(e) => updateGooglePref({ pull_google_events: e.target.checked }, 'pull')}
                            disabled={savingPref === 'pull'}
                          />
                          <span>
                            Afficher <strong>aussi mes événements Google</strong> dans le calendrier OFManager
                            <span className="text-[10.5px] text-[#047857] italic ml-1">(lecture seule, sync toutes les 15 min)</span>
                          </span>
                        </label>
                        {googlePrefs?.pull_google_events && (
                          <button
                            type="button"
                            onClick={async () => {
                              setSavingPref('pull_now');
                              try {
                                const { data } = await api.post('/integrations/google/pull-now');
                                toast({ title: 'Import terminé', description: `${data.imported} événements importés depuis Google` });
                              } catch (err) {
                                toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Import impossible' });
                              } finally { setSavingPref(null); }
                            }}
                            disabled={savingPref === 'pull_now'}
                            data-testid="email-gmail-pull-now"
                            className="ml-5 h-7 px-2.5 rounded-md text-[11px] font-semibold border border-[#A7F3D0] bg-white text-[#047857] hover:bg-[#ECFDF5] disabled:opacity-60"
                          >
                            {savingPref === 'pull_now' ? 'Import en cours…' : 'Importer maintenant'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* -------- SMTP form (user-scope ONLY — hidden by default, expanded
                 only when user clicks "Connecter" on the SMTP row OR when an
                 SMTP config already exists for this user) -------- */}
            {scope === 'user' && (smtpFormOpen || existing) && (
              <div className="border-t border-[#EFF0F6] pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[13px] font-bold text-[#1A1B2E]">
                    Configuration SMTP / IMAP
                  </h4>
                  {!existing && smtpFormOpen && (
                    <button
                      type="button"
                      onClick={() => setSmtpFormOpen(false)}
                      data-testid="email-smtp-close"
                      className="h-7 px-2.5 rounded-md text-[11px] text-[#6B6C85] hover:bg-[#F6F7FB]"
                    >
                      Masquer
                    </button>
                  )}
                </div>
                <EmailSmtpForm
                  providers={providers}
                  form={form}
                  setForm={setForm}
                  applyPreset={applyPreset}
                  hasExisting={!!existing}
                  onTestImap={handleTestImap}
                  testingImap={testingImap}
                  testImapResult={testImapResult}
                />
              </div>
            )}

            {/* -------- Signature (user-scope: replaced by UniversalSignatureEditor above; legacy hidden) */}

            {/* -------- Test + Save (user-scope ONLY — only when SMTP form is visible) -------------------- */}
            {scope === 'user' && (smtpFormOpen || existing) && (
            <div className="mt-5 pt-4 border-t border-[#EFF0F6] grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
              <div>
                <label className="text-[12.5px] font-semibold text-[#4A4B63] block mb-1.5">Envoyer un email de test à</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  data-testid="email-test-to"
                  placeholder="vous@exemple.com"
                  className="w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px]"
                />
              </div>
              <button
                type="button"
                onClick={handleTest}
                disabled={testing || effectiveMode === 'gmail'}
                title={effectiveMode === 'gmail' ? 'Gmail OAuth — l\'envoi se fait directement via votre compte Google' : ''}
                data-testid="email-test-btn"
                className="h-10 px-4 rounded-xl bg-white border border-[#C7D2FE] text-[#4F46E5] text-[13px] font-semibold flex items-center gap-2 hover:bg-[#EEF2FF] disabled:opacity-60"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Tester
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                data-testid="email-save-btn"
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
            )}

            {scope === 'user' && testResult && (
              <div
                className={`mt-3 rounded-xl p-3 border text-[12.5px] ${testResult.ok ? 'bg-[#E6FBF7] border-[#99F6E4] text-[#0F766E]' : 'bg-[#FEF2F2] border-[#FECACA] text-[#991B1B]'}`}
                data-testid="email-test-result"
              >
                <div className="flex items-start gap-2">
                  {testResult.ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />}
                  <div className="flex-1">
                    <div className="font-semibold mb-1">{testResult.ok ? 'Test réussi' : 'Échec du test SMTP'}</div>
                    <div className="whitespace-pre-line leading-relaxed">{testResult.message}</div>
                  </div>
                  <button type="button" onClick={() => setTestResult(null)} className="text-[11px] opacity-60 hover:opacity-100 shrink-0">✕</button>
                </div>
              </div>
            )}

            {scope === 'user' && existing && (
              <div className="mt-3 flex items-center justify-between pt-3 border-t border-[#EFF0F6]">
                <div className="text-[11.5px] text-[#8E8FA5]">
                  Configuration SMTP enregistrée le {existing.updated_at ? new Date(existing.updated_at).toLocaleDateString('fr-FR') : '—'}
                </div>
                <button
                  type="button"
                  onClick={handleDelete}
                  data-testid="email-delete-btn"
                  className="h-8 px-3 rounded-lg text-[#B91C1C] text-[12.5px] font-semibold flex items-center gap-1 hover:bg-[#FEF2F2]"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// ===========================================================================
// MailboxSwitches — Wave 1u (refactor)
//
// Each user can connect ONE personal mailbox among 3 options:
//   • Gmail (OAuth Google)
//   • Microsoft Outlook (OAuth MS)
//   • SMTP / IMAP custom
//
// UX rule (per user request):
//   - When NONE is connected: 3 options visible with "Connecter" buttons
//   - When ONE is connected: that one shows "✓ Connecté · Par défaut",
//     the 2 other rows are GREYED OUT with "Déconnectez d'abord la boîte
//     active pour changer de fournisseur."
//   - The connected mailbox IS the user's default (no separate toggle).
// ===========================================================================
function MailboxSwitches({ gmailAvailable, googleEmail, hasSmtp, smtpEmail, isAdmin, msStatus, onConnectClick }) {
  const connected = {
    google: !!gmailAvailable,
    microsoft: !!msStatus?.connected,
    smtp: !!hasSmtp,
  };
  // The "primary" connected mailbox (the one we consider default). Priority
  // order = the one the user is most likely to keep:
  //   Microsoft > Gmail > SMTP   (since OAuth boxes are more recent / explicit)
  const primary = connected.microsoft ? 'microsoft' : connected.google ? 'google' : connected.smtp ? 'smtp' : null;
  const anyConnected = !!primary;

  const rows = [
    {
      id: 'google', label: 'Gmail',
      sublabel: connected.google
        ? googleEmail
        : "Connecter votre compte Google pour lire/envoyer depuis Gmail.",
      icon: '✉',
    },
    {
      id: 'microsoft', label: 'Microsoft Outlook',
      sublabel: connected.microsoft
        ? (msStatus?.email || 'Connecté')
        : "Connecter votre compte Microsoft 365 / Outlook.",
      icon: '✉',
    },
    {
      id: 'smtp', label: 'SMTP / IMAP personnalisé',
      sublabel: connected.smtp
        ? (smtpEmail || 'Configuration personnelle active')
        : "OVH, Infomaniak, Zoho… Renseignez les identifiants ci-dessous.",
      icon: '⚙',
    },
  ];

  return (
    <div className="mb-5" data-testid="mailbox-switches">
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide">
          Mes boîtes email
        </label>
        {anyConnected && (
          <span className="text-[11px] text-[#6B6C85]">
            Vous pouvez connecter <strong>une</strong> boîte personnelle à la fois.
          </span>
        )}
      </div>
      <div className="space-y-2">
        {rows.map((r) => {
          const isMe = primary === r.id;
          const isOtherDisabled = anyConnected && !isMe;
          return (
            <div
              key={r.id}
              className={`flex items-center gap-3 p-3 rounded-xl border transition ${
                isMe
                  ? 'border-[#A7F3D0] bg-[#ECFDF5]'
                  : isOtherDisabled
                    ? 'border-[#EFF0F6] bg-[#F6F7FB] opacity-60'
                    : 'border-[#C7D2FE] bg-white hover:bg-[#FAFAFE]'
              }`}
              data-testid={`mailbox-switch-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-[#1A1B2E]">{r.label}</span>
                  {isMe && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#10B981] text-white" data-testid={`mailbox-connected-badge-${r.id}`}>
                      ✓ Connecté · Par défaut
                    </span>
                  )}
                </div>
                <div className={`text-[11.5px] truncate mt-0.5 ${isOtherDisabled ? 'text-[#9CA3AF]' : 'text-[#6B6C85]'}`}>
                  {isOtherDisabled
                    ? "Déconnectez d'abord la boîte active pour changer de fournisseur."
                    : r.sublabel}
                </div>
              </div>
              {/* Action button: connect / disconnect / disabled */}
              {isMe ? (
                <span className="text-[11px] text-[#0F766E] font-semibold">Active</span>
              ) : !anyConnected ? (
                <button
                  type="button"
                  onClick={() => onConnectClick?.(r.id)}
                  data-testid={`mailbox-connect-${r.id}`}
                  className="h-8 px-2.5 rounded-md text-[11.5px] font-semibold border border-[#C7D2FE] text-[#4F46E5] hover:bg-[#EEF2FF]"
                >
                  Connecter
                </button>
              ) : (
                <span className="text-[10.5px] text-[#9CA3AF]">Verrouillé</span>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-2">
        Pour changer de fournisseur, déconnectez d'abord la boîte active ci-dessus (bouton « Déconnecter » sur la carte de gestion qui apparaîtra une fois la connexion établie).
      </p>
    </div>
  );
}
